import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';
import { Observable, of, Subscription } from 'rxjs';
import {
  CallingType,
  DnDidRange,
  MicrosoftDialPlanExceptionGroup,
  PstnConnectivityType,
  RangeType,
} from '../../../../../shared/models/generated/ziroModels';
import {
  ExtensionRangesOptionalValidators,
  ZiroRangeGroup,
} from '../../../../../forms/fields/extension-ranges/ziro-extension-range-models';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../../forms/ziro-forms-models';
import { TranslateService } from '@ngx-translate/core';
import { RangeService } from '../../../../../shared/services/range.service';
import { ZiroExtensionRangesComponent } from '../../../../../forms/fields/extension-ranges/ziro-extension-ranges.component';
import { HtmlInputType, ZiroTextConfig } from '../../../../../forms/fields/text/ziro-text.component';
import {
  ZiroTextareaComponent,
  ZiroTextareaConfig,
} from '../../../../../forms/fields/textarea/ziro-textarea.component';
import { cloneDeep, isEqual } from 'lodash';
import {
  ExtensionRangesDisplayFormComponent,
  RangeDisplayFormModel,
} from '../../../../../forms/fields/extension-ranges/extension-ranges-display-form/extension-ranges-display-form.component';
import { e164BulkNoPlusRegex, e164BulkWithPlusRegex } from '../../../../../shared/e164-extension-validators-regex';
import { ZiroRadioConfig } from '../../../../../forms/fields/radio/ziro-radio.component';
import { ValidatorsService } from '../../../../../shared/validators.service';
import { ButtonStyles } from '../../../../../button/button.component';

export interface MicrosoftExceptionGroupFormData extends MicrosoftDialPlanExceptionGroup {
  bulkRanges: string;
}

@Component({
  selector: 'ziro-microsoft-dial-plan-management-exception-edit-form',
  templateUrl: './dial-plan-exception-management-edit-form.component.html',
  standalone: false,
})
export class MicrosoftDialPlanExceptionManagementEditFormComponent
  extends ZiroFormAbstractDirective<MicrosoftDialPlanExceptionGroup, MicrosoftExceptionGroupFormData>
  implements OnInit, AfterViewInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _translateService = inject(TranslateService);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  readonly groupId = input<number>();
  readonly exceptionGroups = input([] as MicrosoftDialPlanExceptionGroup[]);
  readonly isNewGroup = input<boolean>();
  readonly isValidFromDynamicValidator = output<ZiroFormsValidationState>();

  @ViewChild(ZiroExtensionRangesComponent) extensionRanges: ZiroExtensionRangesComponent;
  @ViewChild(ExtensionRangesDisplayFormComponent) extensionRangesDisplayForm: ExtensionRangesDisplayFormComponent;
  @ViewChild(ZiroTextareaComponent) bulkRanges: ZiroTextareaComponent;

  formConfig: ZiroFormConfig;
  displayRangeFormEntity: RangeDisplayFormModel;
  displayRangeForm = false;
  extensionRangesDisplayFormValidationState: ZiroFormsValidationState;
  displayRangeFormOptionalValidators: ExtensionRangesOptionalValidators;
  validationStates = ZiroFormsValidationState;

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.formConfig = {
      fields: {
        name: {
          label: 'tkey;admin.ms_dial_plan_exception.group_name.label',
          dataAutomation: 'exception-group-name',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          validation: [
            {
              validator: (val: string) => {
                const filteredGroups = this.groupId()
                  ? this.exceptionGroups().filter(
                      (group: MicrosoftDialPlanExceptionGroup) => Number(group.id) !== this.groupId(),
                    )
                  : this.exceptionGroups();
                const hasMatch = filteredGroups.find(
                  (group: MicrosoftDialPlanExceptionGroup) => group.name.toLowerCase() === val.trim().toLowerCase(),
                );
                return hasMatch ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;dialplanmanagement.admin.group.name.alreadyInUse',
            },
          ],
        },
        rangeType: {
          label: 'tkey;admin.ms_dial_plan_exception.edit.range_type.label',
          dataAutomation: 'exception-group-range-type',
          required: true,
          componentConfig: new ZiroRadioConfig({
            buttons: [
              {
                value: CallingType.DID,
                label: 'tkey;admin.ms_dial_plan_exception.edit.range_type.did.label',
              },
              {
                value: CallingType.EXTENSION,
                label: 'tkey;admin.ms_dial_plan_exception.edit.range_type.extension.label',
              },
            ],
            inline: true,
          }),
        },
        bulkRanges: {
          label: 'tkey;admin.ms_dial_plan_exception.edit.bulk_generate.label',
          helpText: () => 'tkey;admin.ms_dial_plan_exception.edit.bulk_generate.helptext',
          dataAutomation: 'exception-group-bulk-ranges',
          componentConfig: new ZiroTextareaConfig({
            placeholder: this._translateService.instant(
              'tkey;admin.ms_dial_plan_exception.edit.bulk_generate.placeholder',
            ),
          }),
          validation: [
            {
              validator: (val: string, rangeType: RangeType) => {
                if (!val) {
                  return ZiroFormsValidationState.VALID;
                }
                const bulkFieldRegex = rangeType === RangeType.DID ? e164BulkWithPlusRegex : e164BulkNoPlusRegex;
                const cleanVal = ValidatorsService.removeFormattingFromBulkRanges(val, rangeType);

                return bulkFieldRegex.test(cleanVal)
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID;
              },
              message: () =>
                this.formData.rangeType === RangeType.DID
                  ? 'tkey;validators.global.e164.invalid.error'
                  : 'tkey;validators.global.extension.invalid.error',
              injectValuesFromFields: ['rangeType'],
            },
          ],
        },
      },
    };

    this.displayRangeFormOptionalValidators = {
      overlappingRanges: true,
      strictE164Validation: this.entity.rangeType === RangeType.DID,
      isExtensionRangeType: this.entity.rangeType === RangeType.EXTENSION,
      hasSameLengthValidation: true,
      overlappingRangesInOtherGroups: {
        groups: this.isNewGroup()
          ? this._mapMsGroupsToGeneric(this.exceptionGroups())
          : this._mapMsGroupsToGeneric(
              this.exceptionGroups().filter((group: MicrosoftDialPlanExceptionGroup) => group.id !== this.entity.id),
            ),
      },
    };
    this.displayRangeFormEntity = { ranges: cloneDeep(this.entity.exceptionRanges) };
    this.displayRangeForm = true;

    const formUpdateSub = this.ziroFormsUpdate$.subscribe(
      (update: ZiroFormsUpdate<MicrosoftDialPlanExceptionGroup>) => {
        this.displayRangeFormOptionalValidators = {
          ...this.displayRangeFormOptionalValidators,
          strictE164Validation: update.new.rangeType === RangeType.DID,
          isExtensionRangeType: update.new.rangeType === RangeType.EXTENSION,
        };
        this._changeDetectorRef.detectChanges();
      },
    );
    this._subscriptions.add(formUpdateSub);
  }

  generateBulkRangesClicked(): void {
    if (!this.formData['bulkRanges'].trim()) {
      return;
    }

    this.displayRangeForm = false;

    // Remove new lines, spaces
    const cleanVal = ValidatorsService.removeFormattingFromBulkRanges(
      this.formData.bulkRanges,
      this.formData.rangeType,
    );
    const generatedRanges = RangeService.generateRangesFromArray(
      cleanVal.replace(/\n|\r/g, '').replace(/ /g, '').split(','),
    );
    const currentRanges = cloneDeep(this.entity.exceptionRanges).filter((r: DnDidRange) => r.start && r.end);
    const updatedRanges = [...currentRanges, ...generatedRanges];
    const newEntity = {
      ...this.entity,
      dialPlanRangesJson: updatedRanges,
    };

    this.formData['bulkRanges'] = '';
    this.entitySource.next(cloneDeep(newEntity));
    this.displayRangeFormEntity = { ranges: newEntity.dialPlanRangesJson };

    setTimeout(() => {
      this.displayRangeForm = true;
    });
  }

  submit(): Observable<null> {
    this.setDisplayFormValidationState();
    return of(null);
  }

  toFormData = (entity: MicrosoftDialPlanExceptionGroup): MicrosoftExceptionGroupFormData => {
    return {
      id: entity.id,
      name: entity.name,
      rangeType: entity.rangeType,
      exceptionRanges: entity.exceptionRanges,
      bulkRanges: this.formData['bulkRanges'] || '',
    };
  };

  toEntity = (formData: MicrosoftExceptionGroupFormData): MicrosoftDialPlanExceptionGroup => {
    return {
      id: formData.id,
      name: formData.name,
      rangeType: formData.rangeType,
      exceptionRanges: formData.exceptionRanges,
    } as MicrosoftDialPlanExceptionGroup;
  };

  onRangeFormUpdate(data: ZiroFormsUpdate<RangeDisplayFormModel>): void {
    // Always update validation state, don't always update entity. Async val will fire this multiple times so entity
    // won't necessarily change onFormUpdate
    if (isEqual(this.entity.exceptionRanges, data.new.ranges)) {
      this.setDisplayFormValidationState();
    } else {
      const newEntity = {
        ...cloneDeep(this.entity),
        exceptionRanges: data.new.ranges,
      };

      this.entitySource.next(newEntity);

      this.setDisplayFormValidationState();
    }
  }

  setDisplayFormValidationState(): void {
    this.extensionRangesDisplayFormValidationState =
      this.isFormSubmitted || this.extensionRangesDisplayForm.extensionRanges.showValidation
        ? this.extensionRangesDisplayForm.validationState
        : this.validationStates.VALID;
  }

  private _mapMsGroupsToGeneric(exceptionGroups: MicrosoftDialPlanExceptionGroup[]): ZiroRangeGroup[] {
    return exceptionGroups.map((g: MicrosoftDialPlanExceptionGroup) => {
      return {
        id: Number(g.id),
        name: g.name,
        ranges: g.exceptionRanges,
        rangeType: CallingType.DID,
        pstnConnectivityType: PstnConnectivityType.DIRECT_ROUTING,
        mainNumber: '',
      };
    });
  }

  protected readonly ButtonStyles = ButtonStyles;
}
